import { useCallback, useEffect, useRef, useState } from 'react';
import { autoplannerAPI } from '../services/api';

const useResolutions = (taskId, options = {}) => {
  const { onSaved } = options;
  const [perDayResolutions, setPerDayResolutions] = useState({}); // { userId: { date: action } }
  const [isSaving, setIsSaving] = useState(false);
  const autosaveTimerRef = useRef(null);

  const loadResolutions = useCallback(async () => {
    try {
      if (!taskId) return;
      const result = await autoplannerAPI.getResolutions(taskId);
      if (result?.success) {
        const backendResolutions = result.resolutions || {};
        setPerDayResolutions(backendResolutions);
      }
    } catch (e) {
      console.error('Error loading resolutions:', e);
    }
  }, [taskId]);

  const queueAutosave = useCallback(
    (next) => {
      if (!taskId) return;
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = setTimeout(async () => {
        try {
          setIsSaving(true);
          const res = await autoplannerAPI.storeResolutions(taskId, next);
          if (res?.success) {
            setPerDayResolutions(next);
            if (typeof onSaved === 'function') onSaved();
          }
        } catch (e) {
          console.error('Autosave resolutions failed:', e);
        } finally {
          setIsSaving(false);
        }
      }, 600);
    },
    [taskId, onSaved]
  );

  const setResolution = useCallback(
    (userId, action, date) => {
      setPerDayResolutions((prev) => {
        const nextUser = { ...(prev[userId] || {}) };
        if (date) {
          const dateStr =
            typeof date === 'string'
              ? date.split('T')[0]
              : new Date(date).toISOString().split('T')[0];
          nextUser[dateStr] = action;
        } else {
          // apply global action across existing dates for the user
          Object.keys(nextUser).forEach((d) => {
            nextUser[d] = action;
          });
        }
        const next = { ...prev, [userId]: nextUser };
        queueAutosave(next);
        return next;
      });
    },
    [queueAutosave]
  );

  const getCurrent = useCallback(
    (userId, date) => {
      const userRes = perDayResolutions[userId] || {};
      const dateStr =
        typeof date === 'string' ? date.split('T')[0] : new Date(date).toISOString().split('T')[0];
      return userRes[dateStr] || '';
    },
    [perDayResolutions]
  );

  const hasApplied = useCallback(
    (userId, date) => {
      const userRes = perDayResolutions[userId] || {};
      const dateStr =
        typeof date === 'string' ? date.split('T')[0] : new Date(date).toISOString().split('T')[0];
      return userRes[dateStr] !== undefined && userRes[dateStr] !== 'ignore';
    },
    [perDayResolutions]
  );

  useEffect(() => {
    loadResolutions();
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, [loadResolutions]);

  return {
    perDayResolutions,
    isSaving,
    loadResolutions,
    setResolution,
    getCurrent,
    hasApplied,
    setPerDayResolutions,
  };
};

export default useResolutions;
